/**
 * The analytics catalog for the lake: the column roles a deployment binds, the raw tables the
 * lake ingests, the fact tables built from them and the columns those carry, and the calendar
 * periods resolve on. Consumers are the fact declarations and builds in `cloud_run/analytics/`,
 * the named queries and their provenance, and the binding UI.
 *
 * Every id below but `ArRawTableId` is a literal union, so `string` is not assignable and a caller
 * value reaches SQL only by indexing a registry with it. `ArRawTableId` is a template type that
 * admits any string carrying the ArTable prefix, quote- or path-bearing ones included, so the type
 * alone proves nothing about shape. Trusted values come from `isArRawTableId` or from a bindings
 * document parsed by `parseAnalyticsBindings`, which admits a table only through that guard.
 */

import type { AR_TABLE_COLLECTION_PREFIX } from './System.Artifacts.types';

/**
 * The code half of the code/config seam. Code names the role; each organisation's
 * `parameters/analyticsBindings` document binds the role to one catalog ArTable, an `ArRawTableId`,
 * and a concrete `TypeId` with an optional `Fields` path; the fact build reads roles out of the raw
 * layer into typed fact columns. Code never names a customer's TypeId or table, and configuration
 * never names a column or SQL. The lake ingests exactly the tables the bindings name.
 */
export type ColumnRoleId =
  | 'salespersonMemberId'
  | 'officeMemberId'
  | 'territoryMemberId'
  | 'manufacturerMemberId'
  | 'customerMemberId'
  | 'salesCreditAmount'
  | 'salesCreditDate'
  | 'quoteAmount'
  | 'quoteDate'
  | 'quoteHasSpecifier'
  | 'orderAmount'
  | 'orderDate'
  | 'lineItemStatus'
  | 'lineItemKind'
  | 'lineItemType'
  | 'lineItemDescription'
  | 'manufacturerName'
  | 'lineItemReleaseDate'
  | 'lineItemPromisedDate'
  | 'lineItemQuantity'
  | 'lineItemShipped'
  | 'lineItemShipDate'
  | 'invoiceOutstandingAmount'
  | 'invoiceDate'
  | 'invoiceNumber'
  | 'invoiceStatus'
  | 'invoiceDueDate'
  | 'invoiceAmount'
  | 'goalTarget'
  | 'goalPeriodStart'
  | 'goalPeriodEnd';

/**
 * The prefixed collection id of one ArTable, such as `_art_ar_salesCreditTable`. Any prefixed string
 * is assignable; only `isArRawTableId` checks the full identifier shape.
 */
export type ArRawTableId = `${typeof AR_TABLE_COLLECTION_PREFIX}${string}`;

/** One raw table per source collection: the fixed product collections, plus the bound ArTables. */
export type RawTableId = 'formVersions' | 'records' | 'goals' | ArRawTableId;

/** A raw table with a source collection, which the nightly rebuild and the change feed write. `goals` is not ingested: goals stay in Firestore and React reads them. */
export type RebuiltRawTableId = Exclude<RawTableId, 'goals'>;

/**
 * The conformed fact tables the runtime reads; it never reads a raw table. The declarations
 * are service-internal to `cloud_run/analytics/src/semantic/FACT_TABLES.ts` and only the ids
 * are shared, because the fact declarations, the named queries and a result's provenance name
 * them.
 */
export const FACT_TABLE_IDS = ['fact_sales', 'fact_lineItems', 'fact_forms', 'fact_projects'] as const;

export type FactTableId = (typeof FACT_TABLE_IDS)[number];

/**
 * Every column a fact table may carry. `docPath` names the one source document a fact row
 * came from, which is what makes an erasure request resolvable to its rows. It is the primary
 * key of a table that does not split; a table that splits a document into one row per selected
 * person keys on `docPath` plus that person's column. `teamIds` is the list of teams booked for
 * that person on the source document, never re-read from the person's current record, and empty
 * for a row with no named person or no stamp. `stage` is the
 * owning form's type and `state` the form version's state, both as stored, unmapped. `formId`
 * names the owning form, `projectId` the project record it references, and `ancestorFormIds` the
 * forms its timeline descends from, its own included. `promisedDate` is the date a line was
 * promised, and `lineQuotedAmount` and `lineOrderedAmount` are a line's whole quoted and ordered
 * amounts, carried unsplit like `lineItemStatus` beside the split `quotedAmount` and `orderedAmount`. `invoiceNumber`, `invoiceStatus`,
 * `invoiceDate` and `invoiceDueDate` are a line's invoice, and `invoicedAmount` and
 * `outstandingAmount` its invoiced and outstanding amounts, split like `orderedAmount`, beside
 * the whole-line `lineInvoicedAmount` and `lineOutstandingAmount`, carried unsplit.
 * `formNumber` is the owning form version's `parentFormNumber` and `projectName` the referenced
 * project record's `name`, both fixed product fields read with no role, null when empty.
 * `itemType`, `itemDescription`, `manufacturerName` and `quantity` are a line's item, by role.
 * `occurredAt` belongs to an instant table and `periodStart` / `periodEnd` to a period one;
 * `salespersonMemberId` is on every table, so that no fact can be selected without a scope
 * predicate. The list order is the order a fact table's columns are built in.
 */
export const FACT_COLUMN_IDS = [
  'docPath',
  'formId',
  'occurredAt',
  'periodStart',
  'periodEnd',
  'releaseDate',
  'promisedDate',
  'invoiceDate',
  'invoiceDueDate',
  'salespersonMemberId',
  'officeMemberId',
  'teamIds',
  'territoryMemberId',
  'manufacturerMemberId',
  'customerMemberId',
  'stage',
  'state',
  'projectId',
  'ancestorFormIds',
  'formNumber',
  'projectName',
  'itemType',
  'itemDescription',
  'manufacturerName',
  'lineItemStatus',
  'lineItemKind',
  'invoiceNumber',
  'invoiceStatus',
  'amount',
  'quotedAmount',
  'orderedAmount',
  'lineQuotedAmount',
  'lineOrderedAmount',
  'invoicedAmount',
  'lineInvoicedAmount',
  'hasSpecifier',
  'quantity',
  'shipped',
  'outstandingAmount',
  'lineOutstandingAmount',
  'target',
] as const;

export type FactColumnId = (typeof FACT_COLUMN_IDS)[number];

/** The days a week may start on, in ISO order: `monday` is ISO weekday 1. */
export const ANALYTICS_WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

/**
 * The calendar a period resolves on, read from `parameters/analytics`. `timeZone` is an IANA
 * zone, `fiscalYearStartMonth` is 1 for a calendar year and 7 for a July fiscal year, and
 * `weekStartsOn` is the day a week begins.
 */
export type AnalyticsCalendar = {
  timeZone: string;
  fiscalYearStartMonth: number;
  weekStartsOn: (typeof ANALYTICS_WEEKDAYS)[number];
};
